import {
  ArrowUpCircle,
  BarChart3,
  Calendar,
  Clock,
  Package,
  TrendingUp,
} from "lucide-react";
import type {
  CampaignReportResponse,
  LiftMetrics,
  SummaryStats,
} from "../../types";
import { BarChart } from "../../components/BarChart";
import { ClientLogo } from "../../components/ClientLogo";
import { MetricCard } from "../../components/MetricCard";
import { SectionToggleWrapper } from "../../components/SectionToggleWrapper";
import { CARD_BG, SHADOW } from "../../config/theme";
import {
  formatAverageUnits,
  formatCurrencyValue,
  formatLiftPercent,
  formatSales,
  type AjinomotoDailyTrendDatum,
  type AjinomotoLiftSummary,
  ROAS_THRESHOLDS,
} from "./ajinomotoGyozaLayoutData";
import type { SectionToggleControls } from "./AjinomotoGyozaSectionTypes";

const CARD_BACKGROUND_CLASSES = CARD_BG;

interface AjinomotoHeaderProps {
  data: CampaignReportResponse;
}

interface AjinomotoSectionProps {
  sectionControls: SectionToggleControls;
}

interface AjinomotoOverallPerformanceSectionProps extends AjinomotoSectionProps {
  summary: SummaryStats;
  avgDailySales: number;
  avgDailySpend: number;
  averageRoas: number;
}

interface AjinomotoLiftAnalysisSectionProps extends AjinomotoSectionProps {
  liftMetrics: LiftMetrics;
  liftSummary: AjinomotoLiftSummary;
}

interface AjinomotoDailyTrendSectionProps extends AjinomotoSectionProps {
  totalDays: number;
  trendData: AjinomotoDailyTrendDatum[];
}

export function AjinomotoHeader({ data }: AjinomotoHeaderProps) {
  const { products, report_type, records_processed, summary } = data;

  return (
    <div className="mb-8">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Ajinomoto Gyoza - Product Performance Report
          </h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              {report_type === "search" ? "Sponsored Products" : "Display Ads"}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {summary.date_range_start} to {summary.date_range_end}
            </span>
            <span className="flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              {records_processed} records
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {summary.total_days} days
            </span>
            {products && (
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                {products.length} products
              </span>
            )}
          </div>
        </div>
        <div>
          <ClientLogo
            clientId="ajinomoto"
            className="h-16 object-contain"
            fallback={
              <div className="h-16 w-16 bg-gray-200 dark:bg-[#5B5353] rounded" />
            }
          />
        </div>
      </div>
    </div>
  );
}

export function AjinomotoOverallPerformanceSection({
  summary,
  avgDailySales,
  avgDailySpend,
  averageRoas,
  sectionControls,
}: AjinomotoOverallPerformanceSectionProps) {
  return (
    <SectionToggleWrapper
      sectionId="gyoza-overall-performance"
      title="Overall Performance"
      hidden={sectionControls.isSectionHidden("gyoza-overall-performance")}
      onHide={sectionControls.hideSection}
      onRestore={sectionControls.restoreSection}
      className="mb-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5" />
        Overall Performance
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Sales"
          value={formatSales(summary.total_sales)}
          subtitle={`${summary.total_units.toLocaleString()} units sold`}
          trend="up"
        />
        <MetricCard
          title="Avg Daily Sales"
          value={`$${avgDailySales.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`}
          subtitle={`${summary.total_days} days in range`}
          trend={avgDailySales >= 1000 ? "up" : "neutral"}
        />
        <MetricCard
          title="Total Spend"
          value={formatSales(summary.total_spend)}
          subtitle={`$${avgDailySpend.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}/day average`}
          trend="neutral"
        />
        <MetricCard
          title="ROAS"
          value={`${averageRoas.toFixed(2)}x`}
          subtitle={
            averageRoas >= ROAS_THRESHOLDS.EXCELLENT
              ? "Excellent performance"
              : averageRoas >= ROAS_THRESHOLDS.GOOD
                ? "Good performance"
                : "Needs improvement"
          }
          trend={
            averageRoas >= ROAS_THRESHOLDS.EXCELLENT
              ? "up"
              : averageRoas >= ROAS_THRESHOLDS.GOOD
                ? "neutral"
                : "down"
          }
        />
      </div>
    </SectionToggleWrapper>
  );
}

export function AjinomotoLiftAnalysisSection({
  liftMetrics,
  liftSummary,
  sectionControls,
}: AjinomotoLiftAnalysisSectionProps) {
  return (
    <SectionToggleWrapper
      sectionId="gyoza-lift-analysis"
      title="Campaign Lift Analysis"
      hidden={sectionControls.isSectionHidden("gyoza-lift-analysis")}
      onHide={sectionControls.hideSection}
      onRestore={sectionControls.restoreSection}
      className="mb-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <ArrowUpCircle className="w-5 h-5" />
        Campaign Lift Analysis
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div
          className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Baseline Period
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {liftMetrics.baseline_period_start} to {liftMetrics.baseline_period_end}
          </p>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Avg Daily Sales
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                ${formatCurrencyValue(liftSummary.baselineAvgSales)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Avg Daily Units
              </div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatAverageUnits(liftSummary.baselineAvgUnits)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Days in Period
              </div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {liftSummary.baselinePeriodDays.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border-2 border-green-200 p-6`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Lift Period
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {liftMetrics.lift_period_start} to {liftMetrics.lift_period_end}
          </p>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Avg Daily Sales
              </div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                ${formatCurrencyValue(liftSummary.liftAvgSales)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Avg Daily Units
              </div>
              <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                {formatAverageUnits(liftSummary.liftAvgUnits)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Days in Period
              </div>
              <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                {liftSummary.liftPeriodDays.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Performance Lift
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Average daily performance compared to baseline
          </p>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Avg Daily Sales Lift
              </div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatLiftPercent(liftSummary.avgSalesLiftPercentage)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                vs baseline
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Avg Daily Units
              </div>
              <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                {formatAverageUnits(liftSummary.liftAvgUnits)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {formatLiftPercent(liftSummary.avgUnitsLiftPercentage)} vs baseline
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6 mt-6`}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Avg Daily Sales: Baseline vs Lift Period
        </h3>
        <BarChart
          labels={["Baseline Period", "Lift Period"]}
          datasets={[
            {
              label: "Avg Daily Sales",
              data: [liftSummary.baselineAvgSales, liftSummary.liftAvgSales],
              backgroundColor: [
                "rgba(107, 114, 128, 0.8)",
                "rgba(34, 197, 94, 0.8)",
              ],
            },
          ]}
          horizontal={false}
          showLegend={false}
          height="h-64"
        />
      </div>
    </SectionToggleWrapper>
  );
}

export function AjinomotoDailyTrendSection({
  totalDays,
  trendData,
  sectionControls,
}: AjinomotoDailyTrendSectionProps) {
  return (
    <SectionToggleWrapper
      sectionId="gyoza-daily-trend"
      title="Daily Performance Trend"
      hidden={sectionControls.isSectionHidden("gyoza-daily-trend")}
      onHide={sectionControls.hideSection}
      onRestore={sectionControls.restoreSection}
      className="mb-8"
    >
      <div
        className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          Daily Performance Trend
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Sales vs Spend over {totalDays} days
        </p>
        <BarChart
          labels={trendData.map((metric) => metric.label)}
          datasets={[
            {
              label: "Sales ($)",
              data: trendData.map((metric) => metric.sales),
              backgroundColor: "rgba(34, 197, 94, 0.8)",
            },
            {
              label: "Spend ($)",
              data: trendData.map((metric) => metric.spend),
              backgroundColor: "rgba(239, 68, 68, 0.8)",
            },
          ]}
          stacked={false}
          horizontal={false}
          showLegend={true}
          height="h-96"
        />
      </div>
    </SectionToggleWrapper>
  );
}
